/**
 * Check if the current page is a "shared" page.
 *
 * "Shared" represents a subset of pages which are privately (sometimes publicly) shared to customers.
 * Most customers still need (OTP) authentication to access "shared" pages. Some can opt-out.
 *
 * These pages are considered "shared" pages:
 * - theft reports
 * - theft reports - shared investigation
 * - theft reports - shared transaction
 *
 * This global utility is needed because some Auth0 logic lives outside the Angular application (e.g. app.module).
 * Also, some rxjs subscriptions like error$ do not have access to Angular Router.
 *
 * @returns true if the current page is a shared page
 */
export const isSharedPage = () => window.location.pathname.match(/^\/shared(?:\/|$)/);

/**
 * Check if the URL is one of the shared pages an anonymous caller can open (branches that opted out of guest OTP):
 * shared theft reports (except the auth callback) and shared investigations.
 *
 * The API decides whether these actually render for an anonymous caller. Both pages handle a 401 themselves.
 *
 * @returns true if the URL is a shared page that allows anonymous viewing
 */
export const isAnonymousAccessibleSharedUrl = (url: string): boolean => {
  const segments = url.split('?')[0].split('/').filter(Boolean);
  if (segments[0] !== 'shared') return false;

  if (segments[1] === 'theft-report') {
    return segments.length >= 3 && segments[2] !== 'auth';
  }

  if (segments[1] === 'investigation') {
    return segments.length === 3;
  }

  return false;
};

/**
 * Get the redirect URI for shared pages.
 *
 * Passing this to Auth0 authenticate calls
 * avoids another subset of authentication-related issues (edge cases).
 *
 * @returns redirect URI for shared pages
 */
export const getSharedRedirectUri = () => `${window.location.origin}/shared/theft-report/auth/callback`;

/**
 * Get the redirect URI for main app.
 *
 * @returns redirect URI for main app
 */
export const getAppRedirectUri = () => `${window.location.origin}/auth/callback`;

/**
 * Return the maximum integer value for 32-bit signed integers (int.MaxValue in C#).
 *
 * @returns The value is 2147483647
 */
export const MAX_INT32 = 2147483647;

/**
 * Generate a RFC4122 v4 UUID string.
 * Uses `crypto.randomUUID` when available, otherwise falls back to a
 * Math.random-based implementation.
 */
export function generateUuid(): string {
  try {
    const cryptoObj = (globalThis as typeof globalThis).crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return cryptoObj.randomUUID();
    }
  } catch {
    // Fall through to fallback implementation
  }

  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  let result = '';
  for (let i = 0; i < template.length; i += 1) {
    const c = template[i];
    if (c === 'x' || c === 'y') {
      const r = Math.floor(Math.random() * 16);
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      result += v.toString(16);
    } else {
      result += c;
    }
  }
  return result;
}
